const DEFAULT_API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost/api').replace(/\/$/, '');
const DEFAULT_MERCURE_PUBLIC_URL =
  process.env.REACT_APP_MERCURE_PUBLIC_URL || 'http://localhost/.well-known/mercure';
const HOSTED_TENANT_ADMIN_HOST_PATTERN = /^admin-[^.]+\.pnpfxcrm\.com$/i;
const CUSTOM_TENANT_ADMIN_HOST_PATTERN = /^admin[.-]/i;
const LOCAL_HOST_PATTERN = /^(localhost|127\.|0\.0\.0\.0|::1$)/i;

function isTenantAdminHost() {
  if (typeof window === 'undefined' || !window.location) {
    return false;
  }

  const hostname = window.location.hostname || '';
  if (!hostname || LOCAL_HOST_PATTERN.test(hostname)) {
    return false;
  }

  return HOSTED_TENANT_ADMIN_HOST_PATTERN.test(hostname) || CUSTOM_TENANT_ADMIN_HOST_PATTERN.test(hostname);
}

function resolveDefaultApiBaseUrl() {
  if (isTenantAdminHost()) {
    return '/api';
  }

  return DEFAULT_API_BASE_URL;
}

function resolveDefaultMercurePublicUrl() {
  if (isTenantAdminHost()) {
    return '/.well-known/mercure';
  }

  return DEFAULT_MERCURE_PUBLIC_URL;
}

function createDefaultRuntimeConfig() {
  return {
    apiBaseUrl: resolveDefaultApiBaseUrl(),
    mercurePublicUrl: resolveDefaultMercurePublicUrl(),
    tenant: null,
    branding: {},
    features: {},
    available: true,
  };
}

let runtimeConfig = createDefaultRuntimeConfig();

function resolveBootstrapUrl(apiBaseUrl) {
  return `${String(apiBaseUrl || resolveDefaultApiBaseUrl()).replace(/\/$/, '')}/public/bootstrap`;
}

function resolveRuntimeApiBaseUrl(value, bootstrapUrl, fallback) {
  if (isTenantAdminHost()) {
    return '/api';
  }

  const candidate = String(value || fallback).trim();
  if (/^https?:\/\//.test(candidate)) {
    return candidate.replace(/\/$/, '');
  }

  try {
    return new URL(candidate.startsWith('/') ? candidate : `/${candidate}`, bootstrapUrl).toString().replace(/\/$/, '');
  } catch {
    return String(fallback).replace(/\/$/, '');
  }
}

function resolveRuntimeMercurePublicUrl(value, bootstrapUrl, fallback) {
  if (isTenantAdminHost()) {
    return '/.well-known/mercure';
  }

  const candidate = String(value || fallback).trim();
  if (/^https?:\/\//.test(candidate)) {
    return candidate;
  }

  try {
    return new URL(candidate.startsWith('/') ? candidate : `/${candidate}`, bootstrapUrl).toString();
  } catch {
    return String(fallback);
  }
}

export function getRuntimeConfig() {
  return runtimeConfig;
}

export function resetRuntimeConfigForTests() {
  runtimeConfig = createDefaultRuntimeConfig();
}

export async function loadRuntimeConfig(fetchImpl = fetch) {
  const bootstrapUrl = resolveBootstrapUrl(runtimeConfig.apiBaseUrl);

  try {
    const response = await fetchImpl(bootstrapUrl, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      return runtimeConfig;
    }

    const bootstrap = await response.json();
    runtimeConfig = {
      ...runtimeConfig,
      apiBaseUrl: resolveRuntimeApiBaseUrl(bootstrap?.api?.baseUrl, bootstrapUrl, runtimeConfig.apiBaseUrl),
      mercurePublicUrl: resolveRuntimeMercurePublicUrl(
        bootstrap?.api?.mercureHubUrl,
        bootstrapUrl,
        runtimeConfig.mercurePublicUrl,
      ),
      tenant: bootstrap?.tenant || null,
      branding: bootstrap?.branding || {},
      features: bootstrap?.features || {},
      available: bootstrap?.available !== false,
    };
  } catch {
    // Keep build-time defaults when backend bootstrap is not reachable.
  }

  return runtimeConfig;
}
